import React, { createContext, useContext, useEffect, useRef } from 'react'
import {
  useCampsLazyQuery,
  useMyCampsLazyQuery,
  useCampLazyQuery,
  useParticipantsLazyQuery,
  useSpecialTalentsLazyQuery,
  useMarkedSpecialTalentsLazyQuery
} from '../../lib/apollo/types'
import { format } from 'date-fns'
import { useUser } from '../Auth/hooks'

interface BackgroundSyncContextValue {
  isLoading: boolean
  lastSyncTime: Date | null
  syncProgress: string
  triggerSync: (forceNetworkFirst?: boolean) => Promise<void>
}

const BackgroundSyncContext = createContext<BackgroundSyncContextValue>({
  isLoading: false,
  lastSyncTime: null,
  syncProgress: '',
  triggerSync: async () => {}
})

export const useBackgroundSync = () => useContext(BackgroundSyncContext)

const SYNC_INTERVAL = 60 * 60 * 1000 // 1 hour interval
const PRELOAD_DELAY = 2000 // 2 seconds
const MIN_SYNC_INTERVAL = 5 * 60 * 1000 // 5 minutes minimum between syncs

export const BackgroundSyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useUser()
  const [isLoading, setIsLoading] = React.useState(false)
  const [lastSyncTime, setLastSyncTime] = React.useState<Date | null>(null)
  const [syncProgress, setSyncProgress] = React.useState('')
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastSyncAttemptRef = useRef<Date | null>(null)

  const [loadCamps] = useCampsLazyQuery()
  const [loadMyCamps] = useMyCampsLazyQuery()
  const [loadCampDetails] = useCampLazyQuery()
  const [loadParticipants] = useParticipantsLazyQuery()
  const [loadSpecialTalents] = useSpecialTalentsLazyQuery()
  const [loadMarkedSpecialTalents] = useMarkedSpecialTalentsLazyQuery()

  const syncCampData = async (campId: string, campName: string) => {
    try {
      // console.log(`[BackgroundSync] Preloading data for camp: ${campName} (ID: ${campId})`)

      // Load camp details (cache-and-network ensures data is cached for offline use)
      await loadCampDetails({
        variables: { campId },
        fetchPolicy: 'cache-and-network' // Fetch fresh data but ensure it's cached
      })
      // console.log('[BackgroundSync] Camp details cached:', !!campResult.data?.camp)

      // Load participants (cache-and-network ensures data is cached for offline use)
      await loadParticipants({
        variables: { campId },
        fetchPolicy: 'cache-and-network' // Fetch fresh data but ensure it's cached
      })
      // console.log('[BackgroundSync] Participants cached:', participantsResult.data?.participants?.length ?? 0, 'participants')

      // Load special talents (cache-and-network ensures data is cached for offline use)
      await loadSpecialTalents({
        variables: { campId },
        fetchPolicy: 'cache-and-network' // Fetch fresh data but ensure it's cached
      })
      // console.log('[BackgroundSync] Special talents cached:', specialTalentsResult.data?.specialTalents?.length ?? 0, 'talents')

      // Load marked special talents (cache-and-network ensures data is cached for offline use)
      await loadMarkedSpecialTalents({
        variables: { campId },
        fetchPolicy: 'cache-and-network' // Fetch fresh data but ensure it's cached
      })
      // console.log('[BackgroundSync] Marked special talents cached:', markedSpecialTalentsResult.data?.markedSpecialTalents?.length ?? 0, 'marked talents')

      // Small delay between camps to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 500))
    } catch (error) {
      console.error(`[BackgroundSync] Error preloading camp ${campName} (ID: ${campId}):`, error)
    }
  }

  const performSync = async (forceNetworkFirst = false) => {
    if (!navigator.onLine || !user?.user.roles.includes('trainer')) {
      // console.log('[BackgroundSync] Skipping sync - offline or not a trainer')
      return
    }

    // Prevent syncing too frequently
    if (lastSyncAttemptRef.current && !forceNetworkFirst) {
      const timeSinceLastSync = Date.now() - lastSyncAttemptRef.current.getTime()
      if (timeSinceLastSync < MIN_SYNC_INTERVAL) {
        // console.log('[BackgroundSync] Skipping sync - too soon since last sync')
        return
      }
    }

    lastSyncAttemptRef.current = new Date()

    setIsLoading(true)
    setSyncProgress('Starting comprehensive sync...')

    try {
      // Calculate date range: previous month, current month, and next month
      const now = new Date()
      const currentYear = now.getFullYear()
      const currentMonth = now.getMonth()

      // Start of previous month
      const prevMonth = new Date(currentYear, currentMonth - 1, 1)

      // End of next month
      const nextMonth = new Date(currentYear, currentMonth + 2, 0) // Day 0 = last day of previous month

      // console.log(`[BackgroundSync] Caching camps from ${format(prevMonth, 'yyyy-MM-dd')} to ${format(nextMonth, 'yyyy-MM-dd')}`)

      // Use cache-and-network to get fresh data but ensure it's cached for offline use

      // 1. Get camps within 3-month window (previous, current, next month)
      setSyncProgress('Fetching camps for 3-month period...')
      const allCampsResult = await loadCamps({
        variables: {
          start: format(prevMonth, 'yyyy-MM-dd'),
          end: format(nextMonth, 'yyyy-MM-dd')
        },
        fetchPolicy: 'cache-and-network' // Fetch fresh camp list and cache it
      })

      // 2. Get user's assigned camps within same 3-month window
      setSyncProgress('Fetching your assigned camps...')
      const myCampsResult = await loadMyCamps({
        variables: {
          start: format(prevMonth, 'yyyy-MM-dd'),
          end: format(nextMonth, 'yyyy-MM-dd')
        },
        fetchPolicy: 'cache-and-network' // Fetch fresh assignments and cache them
      })

      if (!allCampsResult.data?.camps) {
        throw new Error('Failed to fetch camps list')
      }

      // 3. Preloading: camps from previous, current, and next month for offline support
      const allCamps = [...allCampsResult.data.camps]
        .sort((a, b) => {
          const dateA = new Date(a.start ?? '').getTime()
          const dateB = new Date(b.start ?? '').getTime()
          return dateB - dateA // Most recent first
        })

      // console.log(`[BackgroundSync] Starting preload of ${allCamps.length} camps (3-month window)`)
      setSyncProgress('Syncing camps for offline use...')

      // 4. Prioritize user's camps first
      const userCampIds = new Set(myCampsResult.data?.myCamps?.map(camp => camp.id) ?? [])
      const userCamps = allCamps.filter(camp => userCampIds.has(camp.id))
      const otherCamps = allCamps.filter(camp => !userCampIds.has(camp.id))

      // const totalCamps = allCamps.length

      // Preload user camps first (highest priority)
      for (const camp of userCamps) {
        await syncCampData(camp.id, camp.caption ?? `Camp ${camp.id}`)
      }

      // Preload all other camps
      for (const camp of otherCamps) {
        await syncCampData(camp.id, camp.caption ?? `Camp ${camp.id}`)
      }

      setLastSyncTime(new Date())
      setSyncProgress('Sync complete')
      // console.log(`[BackgroundSync] Successfully preloaded ${totalCamps} camps (3-month window) with all data`)

      // Try to register for background sync if available
      if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
        try {
          const registration = await navigator.serviceWorker.ready
          await registration.sync.register('sync-camp-data-oneshot')
          // console.log('[BackgroundSync] Registered for background sync')
        } catch (error) {
          // console.log('[BackgroundSync] Background sync registration failed:', error)
        }
      }
    } catch (error) {
      // console.error('[BackgroundSync] Error during comprehensive sync:', error)
      setSyncProgress('Sync failed - some camps may not be available offline')
    } finally {
      setIsLoading(false)
      // Clear progress message after 5 seconds
      setTimeout(() => { setSyncProgress('') }, 5000)
    }
  }

  // Initial sync when component mounts and user is authenticated
  useEffect(() => {
    if (user?.user.roles.includes('trainer')) {
      // console.log('[BackgroundSync] Trainer authenticated, starting initial sync (3-month window)')
      // Wait a bit for the app to fully load, then do initial sync
      const initialSyncTimeout = setTimeout(() => {
        performSync(true) // Force network-first for initial sync
      }, PRELOAD_DELAY)

      // Set up interval for periodic syncs (only if this is the first time we see a trainer)
      syncIntervalRef.current = setInterval(() => {
        performSync()
      }, SYNC_INTERVAL)

      return () => {
        clearTimeout(initialSyncTimeout)
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current)
        }
      }
    }
  }, [user?.user.roles.includes('trainer')])

  // Sync when coming back online
  useEffect(() => {
    const handleOnline = () => {
      // console.log('[BackgroundSync] Network restored, syncing data...')
      performSync()
    }

    window.addEventListener('online', handleOnline)
    return () => { window.removeEventListener('online', handleOnline) }
  }, [user])

  const value: BackgroundSyncContextValue = {
    isLoading,
    lastSyncTime,
    syncProgress,
    triggerSync: performSync
  }

  return (
    <BackgroundSyncContext.Provider value={value}>
      {children}
    </BackgroundSyncContext.Provider>
  )
}
